import { useGiftForm } from '../../../../components/GiftFormContext';
import { useTokenSearch } from '../../../../hooks/useTokenSearch';
import styled, { useTheme } from 'styled-components';
import TextInput from '../../../../components/Inputs/Text';
import { hapticsImpactLight } from '../../../../common/haptic';
import TokenList from '../../../../components/TokenList';
import {
  EVENT_GIFT_FLOW_TOKEN_SELECTED,
  sendEvent,
} from '../../../../common/analytics';
import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
`;

const LPD_PRIORITY_TOKENS = {
  netflix: {
    firstOne: 'XsEH7wWfJJu2ZT3UCFeVfALnVA6CP5ur7Ee11KmzVpL',
    stocksSecond: true,
  },
  gold: {
    firstOne: 'Xsv9hRk1z5ystj9MhnA7Lq4vjSsLwzL2nxrwmwtD3re',
    stocksSecond: true,
  },
  mcdonalds: {
    firstOne: 'XsqE9cRRpzxcGKDXj1BJ7Xmg4GRhZoyY1KpmGSxAWT2',
    stocksSecond: true,
  },
  microsoft: {
    firstOne: 'XspzcW1PRtgf6Wj92HCiZdjzKCyFekVD8P5Ueh3dRMX',
    stocksSecond: true,
  },
  cocacola: {
    firstOne: 'XsaBXg8dU5cPM6ehmVctMkVqoiRG2ZjMo1cyBJ3AykQ',
    stocksSecond: true,
  },
  apple: {
    firstOne: 'XsbEhLAtcf6HdfpFZ5xEMdqW8nfAvcsP5bdudRLJzJp',
    stocksSecond: true,
  },
  stocks: {
    firstOne: 'XsDoVfqeBukxuZHWhdvWHBhgEHjGNst4MLodqsJHzoB',
    stocksSecond: true,
  },
};

const TokenSelector = ({ onContinue, isModal = false }) => {
  const { setValue, showFreeGift } = useGiftForm();
  const theme = useTheme();

  const {
    searchTerm,
    displayTokens,
    isLoading,
    isFetching,
    hasMore,
    setSearchTerm,
    loadMore,
  } = useTokenSearch(null, null);

  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');

  const sortedTokens = useMemo(() => {
    if (lpd) {
      if (!LPD_PRIORITY_TOKENS[lpd]) {
        return displayTokens;
      }

      const { firstOne, stocksSecond } = LPD_PRIORITY_TOKENS[lpd];

      return [...displayTokens].sort((a, b) => {
        const aIsNetflix = a.address === firstOne;
        const bIsNetflix = b.address === firstOne;
        const aHasStock = a.name?.toLowerCase().includes('stock') ?? false;
        const bHasStock = b.name?.toLowerCase().includes('stock') ?? false;

        if (aIsNetflix && !bIsNetflix) return -1;
        if (!aIsNetflix && bIsNetflix) return 1;

        if (stocksSecond) {
          if (aHasStock && !bHasStock) return -1;
          if (!aHasStock && bHasStock) return 1;
        }

        return 0;
      });
    }
    return displayTokens;
  }, [displayTokens, lpd]);

  const handleTokenSelect = (token) => {
    hapticsImpactLight();
    setValue('targetTokenAddress', token.address);
    setValue('network', token.network);
    sendEvent(EVENT_GIFT_FLOW_TOKEN_SELECTED, {
      token: token.address,
      network: token.network,
      is_free_gift: showFreeGift,
    });
    // Auto-advance to next step when token is selected
    if (onContinue) {
      // Small delay to ensure form state is updated
      setTimeout(() => {
        onContinue();
      }, 100);
    }
  };

  // Always show the full token list - same UI for both modal and embedded mode
  return (
    <Container>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label
          style={{ color: theme.text.title, fontSize: 14, fontWeight: 500 }}
        >
          Select an Asset to Gift
        </label>
        <TextInput
          placeholder='Search asset'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <TokenList
        tokens={sortedTokens}
        isLoading={isLoading}
        isFetching={isFetching}
        hasMore={hasMore}
        suggestedToken={lpd ? LPD_PRIORITY_TOKENS[lpd]?.firstOne : null}
        loadMore={loadMore}
        onTokenSelect={handleTokenSelect}
      />
    </Container>
  );
};

export default TokenSelector;
